import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './dash.scss';
import { getCollectionReportSummary, getCollectionReportWeekly, getCollectionReportDaily, DownloadcollectionRawReport } from '../../AdminActionCreator.component';
import BackLogo from '../../../../../images/rewampimages/Chevron.png'
import EqualizerRoundedIcon from '@mui/icons-material/EqualizerRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import { useSelector, useDispatch } from 'react-redux';
import { FILTER_ACTION } from '../../../../../reducers/CommonFilters.reducer';

const API_RAW = "https://reports.infitouch.in/collection-raw";

const COLORS = {
  dueToday: "#9966FF",
  paid: "#36A2EB",
  paymentOverdue: "#FF9F40",
  emiOngoing: "#4BC0C0",
  upcoming: "#FF6384"
};



const AbhicashCharts = ({ pageViewHandler }) => {
  const dispatch = useDispatch();
  const { fromDate, toDate } = useSelector(state => state.FilterReduce.commonFilters);
  const [view, setView] = useState("graph")
  const [level, setLevel] = useState('month');
  const [monthData, setMonthData] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [selMonthIdx, setSelMonthIdx] = useState(null);
  const [selWeekIdx, setSelWeekIdx] = useState(null);
  const [startDate, setStartDate] = useState(fromDate);
  const [endDate, setEndDate] = useState(toDate);

  useEffect(() => {
    setStartDate(fromDate)
    setEndDate(toDate)
    loadMonthly()
  }, [fromDate, toDate]);


  const loadMonthly = async () => {
    let url = ""
    const qp = [];
    qp.push(`start_date=${encodeURIComponent(startDate)}`);
    qp.push(`end_date=${encodeURIComponent(endDate)}`);
    if (qp.length) url += "?" + qp.join("&");

    getCollectionReportSummary(url, callback => {
      console.log("callback", callback)
      setMonthData(callback);
      setLevel('month');
      setSelMonthIdx(null);
      setSelWeekIdx(null);
    })
  };

  const loadWeekly = async (monthIdx) => {
    setSelMonthIdx(monthIdx);
    const monthKey = monthData.month_keys[monthIdx];
    getCollectionReportWeekly(monthKey, callback => {
      setWeekData(callback);
      setLevel('week');
      setSelWeekIdx(null);
    })

  };

  const loadDaily = async (weekIdx) => {
    setSelWeekIdx(weekIdx);
    const weekKey = weekData.keys[weekIdx];

    getCollectionReportDaily(weekKey, callback => {
      console.log("ca", callback)
      setDayData(callback);
      setLevel('day');
    })

  };

  const goToMonth = () => {
    setLevel('month');
    setSelWeekIdx(null);
  };

  const goToWeek = () => {
    if (selMonthIdx === null) {
      goToMonth();
      return;
    }
    setLevel('week');
  };

  const handleReset = () => {
    setLevel('month');
    setSelMonthIdx(null);
    setSelWeekIdx(null);
  };

  const download = (url) => {
    const a = document.createElement('a');
    a.href = url;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadFull = (fmt) => {
    const qp = [];
    qp.push(`start_date=${encodeURIComponent(startDate)}`);
    qp.push(`end_date=${encodeURIComponent(endDate)}`);
    qp.push(`fmt=${fmt}`);

    DownloadcollectionRawReport(`?${qp.join("&")}`, callback => {

    })

  };

  const downloadSelected = (fmt) => {
    let url = null;
    if (level === 'month' && selMonthIdx !== null) {
      const mkey = monthData.month_keys[selMonthIdx];
      url = `?month_start=${encodeURIComponent(mkey)}&fmt=${fmt}`;
    } else if (level === 'week' && selMonthIdx !== null) {
      const mkey = monthData.month_keys[selMonthIdx];
      url = `?month_start=${encodeURIComponent(mkey)}&fmt=${fmt}`;
    } else if (level === 'day' && selWeekIdx !== null) {
      const wkey = weekData.keys[selWeekIdx];
      const start = wkey;
      const end = new Date(wkey);
      end.setDate(end.getDate() + 7);
      const endISO = end.toISOString().slice(0, 10);
      url = `?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(endISO)}&fmt=${fmt}`;
    }
    if (url) {
      DownloadcollectionRawReport(url, callback => {

      })
    };
  };

  const handleBarClick = (data, index) => {
    if (level === 'month') loadWeekly(index);
    else if (level === 'week') loadDaily(index);
  };

  const getCurrentData = () => {
    if (level === 'month') return monthData;
    if (level === 'week') return weekData;
    return dayData;
  };

  const getChartData = (type) => {
    const current = getCurrentData();
    if (!current) return [];

    const labels = current.months || current.labels || [];
    const data = type === 'amounts' ? current.amounts : current.counts;

    return labels.map((label, idx) => ({
      name: label,
      index: idx,
      dueToday: data?.due_today?.[idx] || 0,
      paid: data?.paid?.[idx] || 0,
      paymentOverdue: data?.payment_overdue?.[idx] || 0,
      emiOngoing: data?.emi_ongoing?.[idx] || 0,
      upcoming: data?.emi_upcoming?.[idx] || 0
    }));
  };

  const getPercentageData = (type) => {
    const current = getCurrentData();
    if (!current) return [];

    const labels = current.months || current.labels || [];
    const pctData = type === 'amount' ? current.amount_pct : current.count_pct;

    return labels.map((label, idx) => ({
      name: label,
      index: idx,
      dueToday: pctData?.[idx]?.[0] || 0,
      paid: pctData?.[idx]?.[1] || 0,
      paymentOverdue: pctData?.[idx]?.[2] || 0,
      emiOngoing: pctData?.[idx]?.[3] || 0,
      upcoming: pctData?.[idx]?.[4] || 0
    }));
  };

  const getTitleSuffix = () => {
    if (level === 'month') return ' — Month-wise';
    if (level === 'week') return ` — Week-wise (${monthData?.months?.[selMonthIdx] || ''})`;
    return ` — Day-wise (${weekData?.labels?.[selWeekIdx] || ''})`;
  };

  const getMonthLabel = () => {
    if (selMonthIdx !== null && monthData?.months?.[selMonthIdx]) {
      return monthData.months[selMonthIdx];
    }
    return 'Month';
  };

  const hasRange =
    (level === 'month' && selMonthIdx !== null) ||
    (level === 'week' && selMonthIdx !== null) ||
    (level === 'day' && selWeekIdx !== null);

  return (
    <div className="abhicash-charts" style={{ marginTop: '-10px' }}>
      <div className="container-fluid">
        <div className='headerss'>
          <div style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }} onClick={(e) => pageViewHandler(e, "landingPage")}>
            <img src={BackLogo} alt="" width={'15%'} style={{ marginRight: '10px', cursor: 'pointer' }} />

            <h3 className="title" style={{ display: 'inline-block' }} >
              Overall Collection

            </h3>
          </div>
        </div>
        <div className="header-section">
          <nav className="breadcrumb">
            <span className="breadcrumb-item" onClick={goToMonth}>
              {getMonthLabel()}
            </span>
            {(level === 'week' || level === 'day') && (
              <>
                <span className="separator"> / </span>
                <span className="breadcrumb-item" onClick={goToWeek}>
                  {weekData?.labels?.[selWeekIdx ?? 0] || 'Week'}
                </span>
              </>
            )}
            {level === 'day' && (
              <>
                <span className="separator"> / </span>
                <span className="breadcrumb-item active">Day</span>
              </>
            )}
          </nav>
          <div className="button-group">
            <button className="btn btn-success button-pad" onClick={() => downloadFull('xlsx')}>
              Full Excel
            </button>
            <button className="btn btn-outline-success button-pad" onClick={() => downloadFull('csv')}>
              Full CSV
            </button>
            <button
              className="btn btn-outline-secondary button-pad"
              disabled={level === 'month'}
              onClick={goToMonth}
            >
              Back to Month
            </button>
            <button
              className="btn btn-outline-secondary button-pad"
              disabled={level !== 'day'}
              onClick={goToWeek}
            >
              Back to Week
            </button>
            <button
              className="btn btn-outline-secondary button-pad"
              disabled={level === 'month'}
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              className="btn btn-primary button-pad"
              disabled={!hasRange}
              onClick={() => downloadSelected('xlsx')}
            >
              Selected Range Excel
            </button>
            <button
              className="btn btn-outline-primary button-pad"
              disabled={!hasRange}
              onClick={() => downloadSelected('csv')}
            >
              Selected Range CSV
            </button>
          </div>
        </div>

        <div className="date-filters" style={{ marginTop: '20px' }}>
          <div className="row">
            <div className="col-sm-2 col-xs-12">
              <div className="filter-group">
                <label>Start date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {setStartDate(e.target.value);dispatch({ type: FILTER_ACTION, payload: { fromDate: e.target.value } });}}
                />
              </div>
            </div>
            <div className="col-sm-2 col-xs-12">
              <div className="filter-group">
                <label>End date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {setEndDate(e.target.value);dispatch({ type: FILTER_ACTION, payload: { toDate: e.target.value } });}}
                />
              </div>
            </div>
            <div className="col-sm-1 col-xs-12">
              <div className="filter-group">
                <button className="btn btn-primary " style={{ marginTop: '18px', padding: '6px' }} onClick={loadMonthly}>
                  Apply
                </button>
              </div>
            </div>
            <div className="col-sm-2 col-xs-12" style={{ float: 'right' }}>
              <div className="filter-group-new">
                <button className={`custombtn ${view == "graph" ? "active" : ""}`} onClick={() => setView("graph")}>
                  <EqualizerRoundedIcon /> Graph
                </button>
                <button className={`custombtn ${view == "table" ? "active" : ""}`} onClick={() => setView("table")}>
                  <TableRowsRoundedIcon /> Table
                </button>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header">Amounts (₹) — Stacked{getTitleSuffix()}</div>
            <div className="chart-body">
              {
                view == "graph" ?
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={getChartData('amounts')} onClick={(e) => e?.activeLabel && handleBarClick(e, e.activeTooltipIndex)}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 30,
                        bottom: 15,
                      }}
                      barSize={30}
                    >

                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12, angle: -15, textAnchor: 'end', }} />
                      <YAxis axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12 }} />
                      <Tooltip content={<CustomTooltip percentage={false} />} />
                      <Legend content={<CustomLegend />} />
                      <Bar dataKey="dueToday" stackId="a" fill={COLORS.dueToday} name="Due Today" />
                      <Bar dataKey="paid" stackId="a" fill={COLORS.paid} name="Paid" />
                      <Bar dataKey="paymentOverdue" stackId="a" fill={COLORS.paymentOverdue} name="Payment Overdue" />
                      <Bar dataKey="emiOngoing" stackId="a" fill={COLORS.emiOngoing} name="EMI OnGoing" />
                      <Bar dataKey="upcoming" stackId="a" fill={COLORS.upcoming} name="Upcoming" />
                    </BarChart>
                  </ResponsiveContainer>
                  : ""}
              {
                view == "table" ?
                  <div className='customTableCollection ' style={{ maxHeight: '340px', scrollbarWidth: 'thin', overflow: 'auto' }}>
                    <table className='custom-table'>
                      <thead style={{ position: 'sticky', top: '0' }}>
                        <tr>
                          <th>Month</th>
                          <th>Due Today</th>
                          <th>Paid</th>
                          <th>Payment Overdue</th>
                          <th>EMI On Going</th>
                          <th>Upcoming</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          getChartData('amounts') && getChartData('amounts')?.length > 0 ? getChartData('amounts')?.map((value, id) => {
                            return (
                              <tr key={id} onClick={(e) => handleBarClick(e, id)}>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {value?.name || ""}
                                </td>
                                <td>{value?.dueToday || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paid || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paymentOverdue || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.emiOngoing || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.upcoming || <span style={{ opacity: '0.2' }}>0</span>}</td>
                              </tr>
                            )
                          })
                            : ""}
                      </tbody>
                    </table>
                  </div>
                  : ""}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">Counts (EMIs) — Stacked{getTitleSuffix()}</div>
            <div className="chart-body">
              {
                view == "graph" ?
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={getChartData('counts')} onClick={(e) => e?.activeLabel && handleBarClick(e, e.activeTooltipIndex)}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 20,
                        bottom: 5,
                      }}
                      barSize={30}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12, angle: -15, textAnchor: 'end', }} />
                      <YAxis axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12 }} />
                      <Tooltip content={<CustomTooltip percentage={false} />} />
                      <Legend content={<CustomLegend />} />
                      <Bar dataKey="dueToday" stackId="a" fill={COLORS.dueToday} name="Due Today" />
                      <Bar dataKey="paid" stackId="a" fill={COLORS.paid} name="Paid" />
                      <Bar dataKey="paymentOverdue" stackId="a" fill={COLORS.paymentOverdue} name="Payment Overdue" />
                      <Bar dataKey="emiOngoing" stackId="a" fill={COLORS.emiOngoing} name="EMI OnGoing" />
                      <Bar dataKey="upcoming" stackId="a" fill={COLORS.upcoming} name="Upcoming" />
                    </BarChart>
                  </ResponsiveContainer>
                  : ""}
              {
                view == "table" ?
                  <div className='customTableCollection ' style={{ maxHeight: '340px', scrollbarWidth: 'thin', overflow: 'auto' }}>
                    <table className='custom-table'>
                      <thead style={{ position: 'sticky', top: '0' }}>
                        <tr>
                          <th>Month</th>
                          <th>Due Today</th>
                          <th>Paid</th>
                          <th>Payment Overdue</th>
                          <th>EMI On Going</th>
                          <th>Upcoming</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          getChartData('counts') && getChartData('counts')?.length > 0 ? getChartData('counts')?.map((value, id) => {
                            return (
                              <tr key={id} onClick={(e) => handleBarClick(e, id)}>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {value?.name || ""}
                                </td>
                                <td>{value?.dueToday || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paid || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paymentOverdue || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.emiOngoing || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.upcoming || <span style={{ opacity: '0.2' }}>0</span>}</td>
                              </tr>
                            )
                          })
                            : ""}
                      </tbody>
                    </table>
                  </div>
                  : ""}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">Amount % (Row-wise 100%){getTitleSuffix()}</div>
            <div className="chart-body">
              {
                view == "graph" ?
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={getPercentageData('amount')} onClick={(e) => e?.activeLabel && handleBarClick(e, e.activeTooltipIndex)}
                      margin={{
                        top: 20,
                        right: 10,
                        left: 10,
                        bottom: 5,
                      }}
                      barSize={30}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12, angle: -15, textAnchor: 'end', }} />
                      <YAxis axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12 }} />
                      <Tooltip content={<CustomTooltip percentage={true} />} />
                      <Legend content={<CustomLegend />} />
                      <Bar dataKey="dueToday" stackId="a" fill={COLORS.dueToday} name="Due Today" />
                      <Bar dataKey="paid" stackId="a" fill={COLORS.paid} name="Paid" />
                      <Bar dataKey="paymentOverdue" stackId="a" fill={COLORS.paymentOverdue} name="Payment Overdue" />
                      <Bar dataKey="emiOngoing" stackId="a" fill={COLORS.emiOngoing} name="EMI OnGoing" />
                      <Bar dataKey="upcoming" stackId="a" fill={COLORS.upcoming} name="Upcoming" />
                    </BarChart>
                  </ResponsiveContainer>
                  : ""}
              {
                view == "table" ?
                  <div className='customTableCollection ' style={{ maxHeight: '340px', scrollbarWidth: 'thin', overflow: 'auto' }}>
                    <table className='custom-table'>
                      <thead style={{ position: 'sticky', top: '0' }}>
                        <tr>
                          <th>Month</th>
                          <th>Due Today</th>
                          <th>Paid</th>
                          <th>Payment Overdue</th>
                          <th>EMI On Going</th>
                          <th>Upcoming</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          getPercentageData('amount') && getPercentageData('amount')?.length > 0 ? getPercentageData('amount')?.map((value, id) => {
                            return (
                              <tr key={id} onClick={(e) => handleBarClick(e, id)}>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {value?.name || ""}
                                </td>
                                <td>{value?.dueToday || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paid || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paymentOverdue || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.emiOngoing || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.upcoming || <span style={{ opacity: '0.2' }}>0</span>}</td>
                              </tr>
                            )
                          })
                            : ""}
                      </tbody>
                    </table>
                  </div>
                  : ""}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">Count % (Row-wise 100%){getTitleSuffix()}</div>
            <div className="chart-body">
              {
                view == "graph" ?

                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={getPercentageData('count')} onClick={(e) => e?.activeLabel && handleBarClick(e, e.activeTooltipIndex)}
                      margin={{
                        top: 20,
                        right: 10,
                        left: 10,
                        bottom: 5,
                      }}
                      barSize={30}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12, angle: -15, textAnchor: 'end', }} />
                      <YAxis axisLine={false} tick={{ fontSize: window.innerWidth > 1800 ? 15 : 12 }} />

                      <Tooltip content={<CustomTooltip percentage={true} />} />
                      <Legend content={<CustomLegend />} />
                      <Bar dataKey="dueToday" stackId="a" fill={COLORS.dueToday} name="Due Today" />
                      <Bar dataKey="paid" stackId="a" fill={COLORS.paid} name="Paid" />
                      <Bar dataKey="paymentOverdue" stackId="a" fill={COLORS.paymentOverdue} name="Payment Overdue" />
                      <Bar dataKey="emiOngoing" stackId="a" fill={COLORS.emiOngoing} name="EMI OnGoing" />
                      <Bar dataKey="upcoming" stackId="a" fill={COLORS.upcoming} name="Upcoming" />
                    </BarChart>
                  </ResponsiveContainer>
                  : ""}
              {
                view == "table" ?
                  <div className='customTableCollection ' style={{ maxHeight: '340px', scrollbarWidth: 'thin', overflow: 'auto' }}>
                    <table className='custom-table'>
                      <thead style={{ position: 'sticky', top: '0' }}>
                        <tr>
                          <th>Month</th>
                          <th>Due Today</th>
                          <th>Paid</th>
                          <th>Payment Overdue</th>
                          <th>EMI On Going</th>
                          <th>Upcoming</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          getPercentageData('count') && getPercentageData('count')?.length > 0 ? getPercentageData('count')?.map((value, id) => {
                            return (
                              <tr key={id} onClick={(e) => handleBarClick(e, id)}>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {value?.name || ""}
                                </td>
                                <td>{value?.dueToday || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paid || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.paymentOverdue || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.emiOngoing || <span style={{ opacity: '0.2' }}>0</span>}</td>
                                <td>{value?.upcoming || <span style={{ opacity: '0.2' }}>0</span>}</td>
                              </tr>
                            )
                          })
                            : ""}
                      </tbody>
                    </table>
                  </div>
                  : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbhicashCharts;



const CustomTooltip = ({ active, payload, label, percentage }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <p style={{ fontWeight: "600", marginBottom: "6px" }}>{label}</p>
        {payload.map((item) => (
          <p
            key={item.dataKey}
            style={{
              color: item.fill,
              margin: "2px 0",
              fontSize: window.innerWidth > 1800 ? "18px" : "13px",
            }}
          >
            {
              percentage ?
                <>
                  {item.name}: <span style={{ fontWeight: "bold" }}>{item.value.toFixed(2)}%</span>
                </>
                :
                <>
                  {item.name}: <span style={{ fontWeight: "bold" }}>{(Number(item.value))?.toLocaleString("en-us")}</span>
                </>
            }
          </p>
        ))}
      </div>
    );
  }

  return null;
};


const CustomLegend = ({ payload }) => {
  if (!payload || !payload.length) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "16px",
        marginTop: "20px",
      }}
    >
      {payload.map((entry, index) => (
        <div key={`item-${index}`} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: window.innerWidth > 1800 ? "16px" : "12px",
              height: window.innerWidth > 1800 ? "16px" : "12px",
              borderRadius: "50%",
              backgroundColor: entry.color,
            }}
          />
          <span style={{ fontSize: window.innerWidth > 1800 ? "18px" : "13px", color: "#444", fontWeight: 500 }}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
};
